import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Github, Linkedin, Mail, MapPin, Phone, Sparkles, Code2, Database, Cloud, Cpu, Rocket, ChevronRight, Briefcase, GraduationCap, Star, Menu, X } from "lucide-react";
import { Card, CardContent } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Badge } from "./components/ui/badge";

const profile = {
  name: "Usama Abdul Khalique",
  title: "Software Engineer · Full-Stack Engineering Lead",
  location: "Karachi, Sindh, Pakistan",
  phone: "+92 312-1278993",
  email: "hello@usama.dev",
  summary:
    "Full-stack engineering lead with 6+ years building and scaling modern web platforms on AWS and GCP. Experienced in leading distributed teams, migrating monoliths to microservices, and shipping AI-enabled products across prop-tech, fintech, ERP, and healthcare domains.",
  stats: [
    { label: "Years building products", value: "6+" },
    { label: "Developers led", value: "15" },
    { label: "Countries collaborated across", value: "3" },
    { label: "Production incidents reduced", value: "40%" },
  ],
  ctas: {
    primary: "Let’s build your next product",
    secondary: "View experience",
  },
  links: {
    github: "https://github.com/usama967",
    linkedin: "https://www.linkedin.com/in/usama-a-3316b5208/",
    email: "mailto:hello@usama.dev",
  },
};

const experience = [
  {
    role: "Development Manager",
    company: "Culturefy",
    period: "Mar 2023 – Present",
    points: [
      "Lead a 15-member distributed FE/BE/ML team across 3 countries and define technical roadmap for an AI content-personalization platform.",
      "Drive sprint planning, OKRs, and release governance with automated quality gates.",
      "Reduced production incidents by 40% through stronger engineering processes and release controls.",
    ],
  },
  {
    role: "Team Lead / Full-Stack Developer",
    company: "Jamal Brothers",
    period: "Nov 2022 – Mar 2023",
    points: [
      "Led a 6-member team to deliver two MERN-stack projects on schedule.",
      "Introduced a code-review checklist that improved PR quality by 30%.",
      "Designed scalable REST APIs and relational schemas for an e-commerce platform serving 10k+ daily users.",
    ],
  },
  {
    role: "Full-Stack Developer",
    company: "VertikalRMS",
    period: "Mar 2022 – Nov 2022",
    points: [
      "Optimized a property-management SaaS and cut average page load time by 60%.",
      "Improved third-party API sync reliability to 99.9% for core business workflows.",
    ],
  },
  {
    role: "Full-Stack Developer",
    company: "HyperDev Solution",
    period: "Jan 2022 – Nov 2022",
    points: [
      "Delivered 3 MERN applications end-to-end, accelerating time-to-market by 30%.",
      "Implemented automated test suites that reduced critical production bugs by 25%.",
    ],
  },
  {
    role: "Full-Stack Developer",
    company: "Invision Techno",
    period: "Aug 2021 – Dec 2021",
    points: [
      "Built a doctor-patient tele-consultation platform with WebRTC for real-time video interactions.",
      "Deployed a patient-management system for multi-site clinics with secure EHR access and streamlined scheduling.",
    ],
  },
  {
    role: "Full-Stack Developer",
    company: "ASASA Tech",
    period: "Dec 2019 – Dec 2021",
    points: [
      "Built an online payroll system processing salaries for 2k+ employees.",
      "Modernized legacy ERP UX with responsive, component-based Vue.js interfaces.",
    ],
  },
];

const featuredProjects = [
  {
    title: "AI-Driven Content Platform",
    tag: "AI / Personalization",
    description:
      "Led architecture and delivery for recommendation workflows, CMS integrations, and scalable content operations.",
  },
  {
    title: "Property Management SaaS",
    tag: "PropTech",
    description:
      "Owned work-order, lease, and rent modules on the MERN stack with performance and reliability improvements.",
  },
  {
    title: "HR & Payroll Platform",
    tag: "Fintech / ERP",
    description:
      "Built secure full-stack HR features and payroll workflows for high-volume operational environments.",
  },
  {
    title: "Telemedicine Web App",
    tag: "HealthTech",
    description:
      "Implemented real-time consultation flows using Vue.js, Node.js, WebRTC, and Socket.io.",
  },
];

const skillGroups = [
  {
    title: "Frontend",
    icon: Code2,
    items: ["Vue.js", "TypeScript", "JavaScript", "Next.js", "Responsive UI", "State Management"],
  },
  {
    title: "Backend",
    icon: Database,
    items: ["Node.js", "Express.js", "NestJS", "Python", "FastAPI", "PHP", "REST APIs", "GraphQL"],
  },
  {
    title: "Cloud & DevOps",
    icon: Cloud,
    items: ["AWS", "Lambda", "API Gateway", "S3", "DynamoDB", "RDS", "EC2", "IAM", "CloudFormation", "CI/CD", "GitHub Actions", "Docker"],
  },
  {
    title: "AI & Data",
    icon: Cpu,
    items: ["GenAI", "LangChain", "RAG", "LangGraph", "Agents", "Pinecone", "FAISS", "BigQuery", "Firebase", "Firestore"],
  },
];

const education = [
  {
    title: "BS in Computer Science",
    school: "Federal University",
    detail: "Graduating in BSCS",
  },
  {
    title: "Intermediate (HSSC)",
    school: "Guard’s Public College",
    detail: "Completed higher secondary education",
  },
  {
    title: "Matriculation (SSC)",
    school: "Sir Syed Children’s Academy",
    detail: "Completed junior and secondary schooling",
  },
];

const floatingTech = [
  "Vue.js",
  "Node.js",
  "TypeScript",
  "AWS",
  "GCP",
  "FastAPI",
  "NestJS",
  "MongoDB",
  "RAG",
  "Docker",
  "CI/CD",
  "GraphQL",
];

function SectionHeading({ eyebrow, title, description }: { eyebrow: string; title: string; description: string }) {
  return (
    <div className="max-w-2xl space-y-3">
      <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs uppercase tracking-[0.22em] text-white/70">
        <Sparkles className="h-3.5 w-3.5" />
        {eyebrow}
      </div>
      <h2 className="text-3xl font-semibold tracking-tight text-white md:text-4xl">{title}</h2>
      <p className="text-sm leading-7 text-white/65 md:text-base">{description}</p>
    </div>
  );
}

export default function UsamaDeveloperPortfolio() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const nav = useMemo(
    () => [
      { label: "About", href: "#about" },
      { label: "Experience", href: "#experience" },
      { label: "Projects", href: "#projects" },
      { label: "Skills", href: "#skills" },
      { label: "Contact", href: "#contact" },
    ],
    []
  );

  return (
    <div className="min-h-screen bg-[#050816] text-white selection:bg-white/20">
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute left-[-10%] top-[-10%] h-[32rem] w-[32rem] rounded-full bg-fuchsia-500/15 blur-3xl" />
        <div className="absolute right-[-10%] top-[20%] h-[28rem] w-[28rem] rounded-full bg-cyan-400/15 blur-3xl" />
        <div className="absolute bottom-[-10%] left-[25%] h-[30rem] w-[30rem] rounded-full bg-violet-500/10 blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.22) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.22) 1px, transparent 1px)",
            backgroundSize: "52px 52px",
          }}
        />
      </div>

      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#050816]/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 md:px-6 lg:px-8">
          <a href="#top" className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10 shadow-2xl ring-1 ring-white/10">
              <Rocket className="h-5 w-5" />
            </div>
            <div>
              <div className="text-sm font-semibold tracking-wide">Usama.dev</div>
              <div className="text-xs text-white/55">Engineering Leader Portfolio</div>
            </div>
          </a>

          <nav className="hidden items-center gap-8 md:flex">
            {nav.map((item) => (
              <a key={item.label} href={item.href} className="text-sm text-white/70 transition hover:text-white">
                {item.label}
              </a>
            ))}
          </nav>

          <div className="hidden md:block">
            <Button asChild className="rounded-2xl bg-white text-black hover:bg-white/90">
              <a href="#contact">Hire me</a>
            </Button>
          </div>

          <button
            className="rounded-xl border border-white/10 p-2 text-white md:hidden"
            onClick={() => setMobileMenuOpen((v) => !v)}
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="border-t border-white/10 bg-black/30 px-4 py-4 md:hidden">
            <div className="flex flex-col gap-4">
              {nav.map((item) => (
                <a key={item.label} href={item.href} className="text-sm text-white/75" onClick={() => setMobileMenuOpen(false)}>
                  {item.label}
                </a>
              ))}
              <Button asChild className="mt-2 rounded-2xl bg-white text-black hover:bg-white/90">
                <a href="#contact">Hire me</a>
              </Button>
            </div>
          </div>
        )}
      </header>

      <main id="top" className="relative z-10">
        <section className="mx-auto grid max-w-7xl gap-12 px-4 pb-20 pt-14 md:px-6 lg:grid-cols-[1.1fr_0.9fr] lg:px-8 lg:pb-28 lg:pt-20">
          <div className="space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/10 px-4 py-2 text-xs font-medium uppercase tracking-[0.22em] text-cyan-200"
            >
              <Star className="h-3.5 w-3.5" />
              Open to impactful product engineering roles
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 26 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="space-y-6"
            >
              <div className="space-y-4">
                <p className="text-sm uppercase tracking-[0.3em] text-white/45">Software Engineer • Full-Stack • Cloud • AI</p>
                <h1 className="max-w-4xl text-5xl font-semibold leading-[1.02] tracking-tight md:text-7xl">
                  Building
                  <span className="bg-gradient-to-r from-cyan-300 via-white to-fuchsia-300 bg-clip-text text-transparent"> scalable software</span>
                  <br />
                  and leading teams that ship.
                </h1>
                <p className="max-w-2xl text-base leading-8 text-white/70 md:text-lg">
                  {profile.summary}
                </p>
              </div>

              <div className="flex flex-wrap gap-3">
                <Button 
                  size="lg" 
                  className="rounded-2xl bg-white px-6 text-black hover:bg-white/90"
                  onClick={() => {
                    alert("Thanks for your interest! Let's discuss your project.");
                    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  {profile.ctas.primary}
                  <ArrowUpRight className="ml-2 h-4 w-4" />
                </Button>
                <Button asChild size="lg" variant="outline" className="rounded-2xl border-white/15 bg-white/5 px-6 text-white hover:bg-white/10">
                  <a href="#experience">{profile.ctas.secondary}</a>
                </Button>
              </div>

              <div className="flex flex-wrap items-center gap-5 text-sm text-white/60">
                <span className="inline-flex items-center gap-2"><MapPin className="h-4 w-4" /> {profile.location}</span>
                <span className="inline-flex items-center gap-2"><Phone className="h-4 w-4" /> {profile.phone}</span>
                <a className="inline-flex items-center gap-2 transition hover:text-white" href={profile.links.email}><Mail className="h-4 w-4" /> Email</a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4"
            >
              {profile.stats.map((stat, idx) => (
                <Card key={stat.label} className="overflow-hidden rounded-3xl border-white/10 bg-white/5 backdrop-blur-xl">
                  <CardContent className="p-5">
                    <motion.div
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.45, delay: 0.25 + idx * 0.07 }}
                    >
                      <div className="text-3xl font-semibold text-white">{stat.value}</div>
                      <div className="mt-2 text-sm leading-6 text-white/60">{stat.label}</div>
                    </motion.div>
                  </CardContent>
                </Card>
              ))}
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-gradient-to-b from-white/10 to-white/5 p-6 shadow-2xl backdrop-blur-2xl">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(34,211,238,0.18),transparent_30%),radial-gradient(circle_at_bottom_left,rgba(217,70,239,0.16),transparent_28%)]" />
              <div className="relative space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-white/45">Core identity</p>
                    <h3 className="mt-2 text-2xl font-semibold">{profile.name}</h3>
                    <p className="mt-1 text-sm text-white/65">{profile.title}</p>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-black/20 px-3 py-2 text-xs text-white/70">
                    Available for remote-first roles
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  {[
                    { icon: Cloud, title: "Cloud-native systems", text: "AWS, GCP, serverless deployments, IaC, observability-ready workflows." },
                    { icon: Cpu, title: "AI product delivery", text: "GenAI, RAG, agents, LangChain ecosystems, and practical business workflows." },
                    { icon: Briefcase, title: "Leadership & execution", text: "Team leadership, sprint planning, OKRs, code quality, and release governance." },
                    { icon: Database, title: "Scalable backend architecture", text: "Microservices, REST/GraphQL APIs, event-driven systems, and reliable data design." },
                  ].map((item) => {
                    const Icon = item.icon;
                    return (
                      <div key={item.title} className="rounded-3xl border border-white/10 bg-white/5 p-4">
                        <div className="mb-3 inline-flex rounded-2xl bg-white/10 p-2">
                          <Icon className="h-4 w-4" />
                        </div>
                        <h4 className="text-sm font-medium text-white">{item.title}</h4>
                        <p className="mt-2 text-sm leading-6 text-white/60">{item.text}</p>
                      </div>
                    );
                  })}
                </div>

                <div className="rounded-3xl border border-white/10 bg-black/20 p-4">
                  <div className="mb-3 text-xs uppercase tracking-[0.22em] text-white/45">Tech signature</div>
                  <div className="flex flex-wrap gap-2">
                    {floatingTech.map((tech, idx) => (
                      <motion.div
                        key={tech}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.35 + idx * 0.03 }}
                      >
                        <Badge className="rounded-full border border-white/10 bg-white/10 px-3 py-1.5 text-white/80 hover:bg-white/15">
                          {tech}
                        </Badge>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        <section id="about" className="mx-auto max-w-7xl px-4 py-16 md:px-6 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr]">
            <SectionHeading
              eyebrow="About"
              title="A product-minded engineer who can lead and execute"
              description="From coding critical product modules to managing distributed teams, this portfolio is positioned to show a strong mix of hands-on engineering, architecture, leadership, and delivery discipline."
            />

            <div className="grid gap-4 md:grid-cols-2">
              {[
                "Led distributed teams across frontend, backend, and ML functions.",
                "Built systems in prop-tech, fintech, ERP, e-commerce, and health-tech.",
                "Migrated legacy and monolithic systems toward maintainable modern architectures.",
                "Strong with MERN, serverless, CI/CD, cloud infra, and AI-driven workflows.",
              ].map((point) => (
                <div key={point} className="rounded-3xl border border-white/10 bg-white/5 p-5 text-sm leading-7 text-white/70 backdrop-blur-xl">
                  <div className="mb-3 inline-flex rounded-full bg-cyan-400/10 p-2 text-cyan-200">
                    <ChevronRight className="h-4 w-4" />
                  </div>
                  {point}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="experience" className="mx-auto max-w-7xl px-4 py-16 md:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Experience"
            title="Career journey"
            description="A track record of delivering software, improving engineering systems, and building teams that move products forward."
          />

          <div className="mt-12 space-y-6">
            {experience.map((item, idx) => (
              <motion.div
                key={`${item.company}-${item.role}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.45, delay: idx * 0.05 }}
                className="grid gap-4 rounded-[2rem] border border-white/10 bg-white/5 p-6 backdrop-blur-xl lg:grid-cols-[240px_1fr]"
              >
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] text-white/45">{item.period}</div>
                  <div className="mt-2 text-xl font-semibold text-white">{item.role}</div>
                  <div className="mt-1 text-white/60">{item.company}</div>
                </div>
                <div className="space-y-3">
                  {item.points.map((point) => (
                    <div key={point} className="flex gap-3 text-sm leading-7 text-white/70">
                      <span className="mt-2 h-2 w-2 rounded-full bg-cyan-300" />
                      <p>{point}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        <section id="projects" className="mx-auto max-w-7xl px-4 py-16 md:px-6 lg:px-8">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <SectionHeading
              eyebrow="Projects"
              title="Selected product work"
              description="A presentation-ready overview of the types of platforms and systems delivered across multiple industries."
            />
            <div className="text-sm text-white/55">Designed to impress founders, hiring managers, and technical leaders.</div>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {featuredProjects.map((project, idx) => (
              <motion.div
                key={project.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.45, delay: idx * 0.06 }}
              >
                <Card className="h-full rounded-[2rem] border-white/10 bg-gradient-to-b from-white/10 to-white/5 backdrop-blur-xl">
                  <CardContent className="flex h-full flex-col p-6">
                    <Badge className="w-fit rounded-full border border-white/10 bg-white/10 px-3 py-1 text-white/80">{project.tag}</Badge>
                    <h3 className="mt-5 text-2xl font-semibold text-white">{project.title}</h3>
                    <p className="mt-4 flex-1 text-sm leading-7 text-white/65">{project.description}</p>
                    <div className="mt-6 inline-flex items-center gap-2 text-sm text-cyan-200">
                      Explore case-study style presentation <ArrowUpRight className="h-4 w-4" />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </section>

        <section id="skills" className="mx-auto max-w-7xl px-4 py-16 md:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Skills"
            title="Modern stack, cloud depth, and AI capability"
            description="Organized to show breadth without looking cluttered, with a premium portfolio presentation style."
          />

          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {skillGroups.map((group, idx) => {
              const Icon = group.icon;
              return (
                <motion.div
                  key={group.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.45, delay: idx * 0.06 }}
                  className="rounded-[2rem] border border-white/10 bg-white/5 p-6 backdrop-blur-xl"
                >
                  <div className="mb-5 flex items-center gap-3">
                    <div className="rounded-2xl bg-white/10 p-3">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white">{group.title}</h3>
                      <p className="text-sm text-white/55">Production-ready expertise</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {group.items.map((item) => (
                      <Badge key={item} className="rounded-full border border-white/10 bg-black/20 px-3 py-1.5 text-white/80 hover:bg-black/30">
                        {item}
                      </Badge>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-16 md:px-6 lg:px-8">
          <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
            <div className="rounded-[2rem] border border-white/10 bg-white/5 p-6 backdrop-blur-xl md:p-8">
              <div className="flex items-center gap-3">
                <div className="rounded-2xl bg-white/10 p-3"><GraduationCap className="h-5 w-5" /></div>
                <div>
                  <h3 className="text-2xl font-semibold">Education</h3>
                  <p className="text-sm text-white/55">Academic background</p>
                </div>
              </div>
              <div className="mt-8 space-y-4">
                {education.map((item) => (
                  <div key={item.title} className="rounded-3xl border border-white/10 bg-black/20 p-5">
                    <div className="text-lg font-medium text-white">{item.title}</div>
                    <div className="mt-1 text-sm text-white/60">{item.school}</div>
                    <div className="mt-2 text-sm leading-7 text-white/60">{item.detail}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-[2rem] border border-white/10 bg-gradient-to-b from-cyan-400/10 to-fuchsia-400/10 p-6 backdrop-blur-xl md:p-8">
              <div className="text-xs uppercase tracking-[0.22em] text-white/45">Why hire me</div>
              <h3 className="mt-3 text-3xl font-semibold leading-tight">I combine execution speed with engineering leadership.</h3>
              <div className="mt-6 space-y-4 text-sm leading-7 text-white/70">
                <p>I’m strongest where product complexity, team coordination, and architecture quality all matter at the same time.</p>
                <p>I can write code, shape systems, improve delivery processes, and turn technical ambition into production-ready outcomes.</p>
              </div>

              <div className="mt-8 grid grid-cols-2 gap-4">
                {[
                  "MERN",
                  "Microservices",
                  "Serverless",
                  "AI Workflows",
                  "CI/CD",
                  "Distributed Teams",
                ].map((item) => (
                  <div key={item} className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white/80">
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="contact" className="mx-auto max-w-7xl px-4 py-16 md:px-6 lg:px-8 lg:pb-24">
          <div className="rounded-[2.2rem] border border-white/10 bg-white/5 p-6 backdrop-blur-xl md:p-10">
            <div className="max-w-2xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs uppercase tracking-[0.22em] text-white/70">
                <Sparkles className="h-3.5 w-3.5" />
                Contact
              </div>
              <h2 className="mt-4 text-4xl font-semibold tracking-tight">Let’s build something meaningful.</h2>
              <p className="mt-4 text-sm leading-7 text-white/65 md:text-base">
                Looking for a full-stack engineering lead, senior software engineer, or technical product builder? Let’s connect and discuss your platform, roadmap, or scaling challenges.
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-4">
                <a href={profile.links.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-black/20 px-6 py-4 text-sm text-white/80 transition hover:bg-black/30"><Linkedin className="h-5 w-5" /> LinkedIn</a>
                <a href={profile.links.github} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-black/20 px-6 py-4 text-sm text-white/80 transition hover:bg-black/30"><Github className="h-5 w-5" /> GitHub</a>
                <a href={profile.links.email} className="inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-black/20 px-6 py-4 text-sm text-white/80 transition hover:bg-black/30"><Mail className="h-5 w-5" /> Email</a>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
